import { IPublicClientApplication } from "@azure/msal-browser";

/**
 * Deja MSAL listo para usarse: `initialize()` completo y cuenta activa fijada desde el cache.
 *
 * ⚠️ Corre en `main.ts` ANTES de `bootstrapApplication`, no como `provideAppInitializer`.
 *
 * Como initializer competía con `withEnabledBlockingInitialNavigation()`: Angular arranca TODOS los
 * app initializers a la vez, y el del router dispara la navegación inicial (y con ella los guards)
 * mientras este todavía estaba a mitad de `initialize()`. Desde msal-browser v4 el cache de
 * `localStorage` va cifrado y se importa DENTRO de `initialize()`, así que cualquier lectura previa
 * — `getAllAccounts()` en `appAccessGuard`, `getActiveAccount()` en `MsalInterceptor` — revienta con
 * `uninitialized_public_client_application`. Angular corre los `canActivate` de una ruta en paralelo
 * (`combineLatest`), así que `appAccessGuard` NO espera a que `MsalGuard` termine su `initialize()`.
 *
 * El error solo aparecía con cache ya poblado (`getAllAccounts()` sin cuentas ni toca los datos
 * cifrados), o sea: usuario que ya había entrado alguna vez. De ahí que se viera en Edge —el
 * navegador corporativo, donde la sesión ya existía— y no en un perfil limpio de Chrome. Y como el
 * guard tiraba la excepción de forma síncrona dentro de `router.initialNavigation()`, la promesa del
 * initializer del router se rechazaba: bootstrap caído y pantalla en blanco, no un simple error.
 */
export async function initializeMsal(instance: IPublicClientApplication): Promise<void> {
    await instance.initialize();

    // Cuenta activa desde el cache, para que el primer guard ya sepa quién entró.
    //
    // Acá NO se llama a `handleRedirectPromise()`, aunque el initializer viejo lo hacía: la
    // respuesta del login emite `LOGIN_SUCCESS`, y `MsalBroadcastService.msalSubject$` es un
    // `Subject` sin replay. Procesarla antes del bootstrap significa emitirla cuando todavía no
    // existe ni el servicio de broadcast: `AppComponent` se suscribe después y el evento ya pasó,
    // así que nunca corre lo que cuelga de ahí — perfil de Graph y alta del usuario en el primer
    // login. De la respuesta del redirect se encargan `MsalGuard` y `AppComponent`, que ya la
    // esperan (`handleRedirectObservable`) y para entonces sí hay quien escuche.
    if (!instance.getActiveAccount()) {
        const [account] = instance.getAllAccounts();
        if (account) {
            instance.setActiveAccount(account);
        }
    }
}
